import React from 'react';
import { Loader2, CheckCircle, AlertTriangle } from 'lucide-react';

/**
 * How much of the queue is reviewable versus still moving through the pipeline.
 *
 * "waiting" is work that exists but whose previous node hasn't finished, so it
 * cannot be reviewed yet. Showing it means the queue looking short is
 * distinguishable from the queue actually being short.
 */
function ReadinessBar({ readiness, unitLabel = 'items' }) {
  if (!readiness || (!readiness.total && !readiness.stalled)) return null;

  const { ready = 0, waiting = 0, total = 0, stalled = 0 } = readiness;
  const pct = total > 0 ? Math.round((ready / total) * 100) : 0;
  const allReady = waiting === 0;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
      <div className="flex items-center justify-between mb-2 gap-4">
        <div className="flex items-center gap-2 text-sm">
          {allReady ? (
            <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
          ) : (
            <Loader2 className="w-4 h-4 text-indigo-600 animate-spin flex-shrink-0" />
          )}
          <span className="font-medium text-gray-900">
            {ready.toLocaleString()} of {total.toLocaleString()} {unitLabel} ready for review
          </span>
        </div>
        <span className="text-sm font-semibold text-gray-700 flex-shrink-0">{pct}%</span>
      </div>

      <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden flex">
        <div
          className="h-full bg-indigo-600 transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
        {waiting > 0 && (
          <div
            className="h-full bg-amber-300 transition-all duration-500"
            style={{ width: `${100 - pct}%` }}
          />
        )}
      </div>

      <div className="mt-2 text-xs text-gray-500">
        {allReady ? (
          <>Everything in the pipeline is ready — nothing else is on its way.</>
        ) : (
          <>
            <span className="inline-block w-2 h-2 rounded-sm bg-amber-300 align-middle mr-1.5" />
            {waiting.toLocaleString()} still processing upstream and not yet reviewable
          </>
        )}
      </div>

      {/* Work that is not coming on its own - counted separately so the bar
          never promises items that will never arrive. */}
      {stalled > 0 && (
        <div className="mt-2 flex items-start gap-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span>
            {stalled.toLocaleString()} {stalled === 1 ? 'item' : 'items'} stalled — their workflow
            finished without writing a result, so they will not arrive without intervention.
          </span>
        </div>
      )}
    </div>
  );
}

export default ReadinessBar;
